import React, { useEffect, useState } from 'react';
import { RelateTicket } from '../RelateTicketDialog';
import { TicketListCard } from '../TicketListCard/TicketListCard';
import { RelationType } from '../RelateTicketHelper';
import { Dropdown, Flex, Input, Loader, ProviderConsumer, SearchIcon } from '@fluentui/react-northstar';
import { threedigitized } from '../../../../tikit/ticketHelper';
import { appState } from '../../../../AppState';
import { EmptyData } from '../../EmptyData';
import EmptySVG from '../../../../../svg/empty.svg';
import FlashlightSVG from '../../../../../svg/flashlight.svg';
import { useTranslation } from 'react-i18next';
import { TablePagination } from '../../table/TablePagination';
import { LIST_FITS_WIDEST, depthOf, sortTreeFlat, treeRowStyles } from '../../../utils/treeDropdown';

interface TicketsSelectionProps {
  relatedTicketData: RelateTicket;
  setRelatedTicketData?: (relateTicketData: RelateTicket) => void;
  tickets?: Ticket[];
  totalCount: number;
  currentPage: number;
  itemsPerPage: number;
  isLoading: boolean;
  onPageChange: (page: number, pageSize: number) => void;
  onItemsPerPageChange: (count: number) => void;
  onFiltersChange: (searchKey: string, statusId: number) => void;
}

interface DropDownItem {
  key: number,
  header: string,
  styles?: { paddingLeft: string },
  /** The whole name on hover, for one cut short by the list's width. */
  title?: string
}

export const TicketsSelection = ({
  relatedTicketData,
  setRelatedTicketData,
  tickets = [],
  totalCount,
  currentPage,
  itemsPerPage,
  isLoading,
  onPageChange,
  onItemsPerPageChange,
  onFiltersChange
}: TicketsSelectionProps) => {
  const {t} = useTranslation();
  const currentState = appState();
  const [searchKey, setSearchKey] = useState<string>('');
  const [statuses, setStatuses] = useState<DropDownItem[]>([]);
  const [statusId, setStatusId] = useState<number>(0);
  
  useEffect(() => {
    // As a tree: a sub-status indented under its parent, as the ticket's pickers draw it.
    const statusList: DropDownItem[] = sortTreeFlat(currentState.ticketStatus)
      .map(s => ({ key: s.Id, header: s.Value, title: s.Value, styles: treeRowStyles(depthOf(s, currentState.ticketStatus)) }));
    const defaultItem: DropDownItem = { key: 0, header: t('ticket-details.edit-ticket-form.relate-ticket.ticket-selection.all-statuses'), styles: treeRowStyles(0) };
    setStatuses([defaultItem, ...statusList]);
  }, []);
  
  const onStatusChange = (selectedStatusId: number) => {
    setStatusId(selectedStatusId);
    onFiltersChange(searchKey, selectedStatusId);
  }
  
  const onSearchFieldChange = (value: string) => {
    setSearchKey(value);
    onFiltersChange(value, statusId);
  }

  const moveToPage = (type: string) => {
    if (type === "next")
      onPageChange(currentPage + 1, itemsPerPage);
    else if(type === "previous")
      onPageChange(currentPage - 1, itemsPerPage);
    else
      onPageChange(parseInt(type) || 1, itemsPerPage);
  };
  const countPerPage = (count: number) => {
    onItemsPerPageChange(count);
    onPageChange(1, count);
  };
  
  const getHeader = () => {
    switch (relatedTicketData.relationShipType) {
      case RelationType.MergeTickets:
        return `${t('ticket-details.edit-ticket-form.relate-ticket.ticket-selection.select-ticket')} #${threedigitized(
          relatedTicketData.sourceTicketId
        )}`;
      case RelationType.RelateTickets:
        return t('ticket-details.edit-ticket-form.relate-ticket.ticket-selection.relate-select-ticket');
      case RelationType.ParentChild:
        return t('ticket-details.edit-ticket-form.relate-ticket.ticket-selection.make-child');
      case RelationType.BlockedBy:
        return t('ticket-details.edit-ticket-form.relate-ticket.ticket-selection.blocking-ticket');
      default:
        return t('ticket-details.edit-ticket-form.relate-ticket.ticket-selection.select-tickets');
    }
  }
  
  const getDisplayMessage = () => {
    switch (relatedTicketData.relationShipType) {
      case RelationType.MergeTickets:
        return t('ticket-details.edit-ticket-form.relate-ticket.ticket-selection.no-requestor-ticket');
      case RelationType.RelateTickets:
        return t('ticket-details.edit-ticket-form.relate-ticket.ticket-selection.no-available-ticket');
      default:
        return t('ticket-details.edit-ticket-form.relate-ticket.ticket-selection.no-ticket');
    }
  }
  
  const onTicketClick = (ticketId: number) => {
      let relatedTicketIds = [...relatedTicketData.relatedTicketIds];
      if (!relatedTicketIds.includes(ticketId)) relatedTicketIds.push(ticketId);
      else relatedTicketIds.splice(relatedTicketIds.indexOf(ticketId), 1);
      setRelatedTicketData({...relatedTicketData , relatedTicketIds});
  }
  
  const getEmptyIcon = () => {
    return <>
      <Flex className='relate-empty-icon'>
        {searchKey ?
        <EmptyData
          SVGIcon={ <FlashlightSVG width={77} height={77} className={'mb-4'} />}
          headerText={t('ticket-details.edit-ticket-form.relate-ticket.ticket-selection.empty-header')}
          subheaderText={t('common.try-a-different-search')}
        /> :
        <EmptyData
          headerText={getDisplayMessage()}
          SVGIcon={<EmptySVG width={75} height={75} />}
        />}
      </Flex>
    </>;
  }
  
  return (
    <>
      <div className='dialog-header font-bold'>{getHeader()}</div>
      <div className='dialog-filter'>
        {statuses.length > 0 && <>
          <div className={`inline-block mr-2 
          ${relatedTicketData.relationShipType === RelationType.MergeTickets ? 'search-merge' : 'search-relate'}`}>
            <Input fluid icon={<SearchIcon />} defaultValue={searchKey} 
              clearable placeholder={t('ticket-details.edit-ticket-form.relate-ticket.ticket-selection.search-placeholder')}
              onChange={(_: any, event: { value: string; }) => onSearchFieldChange(event.value)}
            />
          </div>
          {relatedTicketData.relationShipType !== RelationType.MergeTickets &&
          (<ProviderConsumer render={globalTheme => (
          // The box filled like the search beside it - bold text alone did not read as a dropdown. The app paints every
          // dropdown box from --mgt-theme-default-background (with !important), unset in this dialog, so it is set here.
          <div className='inline-block status-filter' style={{ '--mgt-theme-default-background': globalTheme.siteVariables.colorScheme.default.background2 } as React.CSSProperties}>
            <Dropdown className='cursor-pointer'
              fluid items={statuses} placeholder={t('ticket-details.edit-ticket-form.relate-ticket.ticket-selection.select-status')}
              value={statuses.find(s => s.key === statusId)}
              onChange={(_: any, event: any) => onStatusChange(event.value["key"])}
              // The box is at the dialog's right edge: a list wider than it grows to the left, and stops at the dialog's width.
              align="end"
              list={{ styles: { ...LIST_FITS_WIDEST, maxWidth: 'min(420px, calc(100vw - 32px))' } }}
            />
          </div>)} />)}
        </>}
      </div>
      <div className='dialog-body-tickets overflow-auto break-all'>
        {isLoading ? <div className={`pt-5 mt-5`}><Loader /></div> :  
        tickets.length > 0 && (
          tickets.map((ticket: Ticket) => (
            <TicketListCard
              key={ticket.Id}
              ticket={ticket}
              isSelected={relatedTicketData.relatedTicketIds.includes(ticket.Id)}
              onClick={() => onTicketClick(ticket.Id)}
            />
          ))
        ) || getEmptyIcon()}
      </div>
      <TablePagination currentPage={currentPage} totalCount={totalCount} moveToPage={moveToPage} itemsPerPage={itemsPerPage} isLoading={isLoading} itemsPerPageChanged={countPerPage} />
    </>
  );
};
